// LoginFormPublic.jsx (JS)

import * as Yup from 'yup';
import { useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
// form
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// routes
import { PATH_AUTH, PATH_DASHBOARD } from '../../../../routes/paths';
// hooks
import useAuth from '../../../../hooks/useAuth';
import useIsMountedRef from '../../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../../components/Iconify';
import { FormProvider, RHFTextField } from '../../../../components/hook-form';
import { getAdminProfile } from '../../../../redux/slices/user';
import errorsText from '../../../../utils/errorsText';
import axiosInstance from 'src/utils/axios';
import { useQueryCustom } from 'src/utils/reactQueryHooks';
import api from 'src/services/api';
import { NATIONAL_CODE_REGEX, PERSIAN_NUMBER_REGEX, RECAPTCHA_SITE_KEY } from 'src/config';
import persianToEnglishNumber from 'src/utils/persianToEnglishNumber';

// NEW
import ReCAPTCHA from 'react-google-recaptcha';

export default function LoginFormPublic({ goToVerify, LoginError, sideQuery, handleSkipVerify }) {
  const { publicLogin } = useAuth();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    code_melli: Yup.string()
      .matches(NATIONAL_CODE_REGEX, errorsText.invalidValue('کدملی'))
      .required(errorsText.blankError()),
    password: Yup.string()
      .matches(PERSIAN_NUMBER_REGEX, errorsText.numberFormat('تلفن همراه'))
      .max(11, errorsText.maxLength(11, 'تلفن همراه'))
      .required(errorsText.blankError()),
    // NEW: captcha required
    // captchaToken: Yup.string().required('لطفاً کپچا را تکمیل کنید'),
  });

  const defaultValues = {
    code_melli: '',
    password: '',
    // NEW
    // captchaToken: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
    mode: 'onSubmit',
  });

  const {
    reset,
    setError,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  // NEW
  const recaptchaRef = useRef(null);

  const onSubmit = async (data) => {
    try {
      // if (!data?.captchaToken) {
      //   setError('captchaToken', { type: 'manual', message: 'لطفاً کپچا را تکمیل کنید' });
      //   return;
      // }

      const codeMelli = persianToEnglishNumber(data.code_melli);
      const phone = persianToEnglishNumber(data.password);

      // If your backend expects captcha in the same login call:
      //   await publicLogin(codeMelli, phone, data.captchaToken);
      // If not, send it inside your own API call:
      //   await axiosInstance.post('/auth/login', { codeMelli, phone, captchaToken: data.captchaToken });

      const resData = await publicLogin(codeMelli, phone);

      goToVerify({
        phone,
        codeMelli: codeMelli,
        expireDate: +new Date() + 240000,
      });

      await handleSkipVerify();

      // token is single-use
      recaptchaRef.current?.reset();
      // setValue('captchaToken', '');
    } catch (error) {
      // reset token for retry
      // recaptchaRef.current?.reset();
      // setValue('captchaToken', '');

      const travelsForm = sideQuery?.data?.data || {};
      const finalFormsArr = [];
      Object.values(travelsForm)?.map((x) => {
        x?.map((y) => {
          const forms = y.forms?.map((z) => ({
            title: `${y?.name} - ${z.name}`,
            path: `${PATH_DASHBOARD.form.root(y.type, y.id)}/${z.name === 'ثبت نام اولیه' || z.name === 'تکمیل ثبت نام' ? 'first' : z.id
              }`,
          }));
          if (forms?.length) finalFormsArr.push(...forms);
        });
      });

      if (error?.response?.status === 408) {
        if (finalFormsArr?.length > 0) {
          navigate(
            finalFormsArr[0].path +
            `?phone=${data.password}&code_melli=${data.code_melli}`
          );
        }
      }

      setError('afterSubmit', { ...error, message: error?.response?.data?.message });
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        {LoginError?.text && LoginError?.text !== '' && (
          <Alert
            severity={LoginError?.type || 'error'}
            icon={<></>}
            sx={{ color: LoginError?.type === 'success' ? '#fff' : undefined }}
          >
            {LoginError?.text || ''}
          </Alert>
        )}

        <RHFTextField name={'code_melli'} label="شماره ملی" />

        <RHFTextField
          name="password"
          label="تلفن همراه"
          type={showPassword ? 'text' : 'text'}
        />

        {/* NEW: reCAPTCHA v2 (Checkbox) with fa locale */}
        {/* <Controller
          control={control}
          name="captchaToken"
          render={({ field }) => (
            <div dir="rtl">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(t) => field.onChange(t || '')}
                onExpired={() => {
                  field.onChange('');
                  recaptchaRef.current?.reset();
                }}
                hl="fa"
                theme="light" // or "dark"
              />
              {errors?.captchaToken && (
                <p style={{ color: '#d32f2f', fontSize: 12, marginTop: 6 }}>
                  {String(errors.captchaToken.message)}
                </p>
              )}
            </div>
          )}
        /> */}
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        color="success"
        variant="contained"
        loading={isSubmitting}
        sx={{ mt: 2 }}
      >
        ورود
      </LoadingButton>
    </FormProvider>
  );
}
